import { useCallback, useRef, useState } from "react";
import { createViewDealDetail } from "@/modules/rev_apac/partners/views/ViewDealDetail";
import type { TDeal } from "@/store";
import type { TApiColumnMappingFileType, TApiColumnMappingFieldType } from "@/apis/proda_finance";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import type { TFilteringRule } from "@/ui/organisms/OrganismFilteringRules";
import { useDealDetailPropcodeVersionActions } from "./wireDealDetailVersions";
import { useDealDetailMappingActions } from "./wireDealDetailMappings";
import { NO_SEGMENTS } from "./noSegments";
import { searchMasterPropertyCodes } from "@/useCases/masterPropertyCodes";

/** Max `limit` the deal mapping endpoints accept (they default to only 100). */
const MAPPING_PAGE_SIZE = 1000;

/**
 * Collect every row from a skip/limit endpoint, paging until `total` is reached.
 *
 * A single un-paged call silently truncates at 100 rows. The deal tabs render
 * this state and their Download buttons serialise it, and since a mapping upload
 * replaces the whole set, a truncated download that gets re-uploaded would
 * delete every mapping past the cap.
 */
export const fetchAllPages = async <T>(
    fetchPage: (skip: number, limit: number) => Promise<{ items: T[]; total: number }>,
): Promise<T[]> => {
    const first = await fetchPage(0, MAPPING_PAGE_SIZE);
    let items = first.items;
    while (items.length < first.total && items.length > 0) {
        const next = await fetchPage(items.length, MAPPING_PAGE_SIZE);
        if (next.items.length === 0) break;
        items = items.concat(next.items);
    }
    return items;
};

/**
 * The loaded deal after a successful update: the request fields the caller
 * actually sent, plus status and its timestamps taken from the server response.
 *
 * The request half is conditional because an omitted field means "no change" to
 * the backend, so echoing `undefined` back would blank a value the server kept.
 * Status is the opposite — read unconditionally from the response, because a save
 * that omitted `is_active` can still find the timestamps moved (the deal may have
 * been toggled from the deals table meanwhile), and the status label reads them to
 * say "as of {when}".
 *
 * Exported for testing: the hook that calls it is not reachable without rendering
 * the whole deal-detail view.
 */
export function applyDealUpdate(
    prev: TDeal,
    data: Parameters<TCompositionDeps["api"]["deals"]["update"]>[1],
    result: Awaited<ReturnType<TCompositionDeps["api"]["deals"]["update"]>>,
): TDeal {
    return {
        ...prev,
        ...(data.name !== undefined && { name: data.name }),
        ...(data.finance_user_ids !== undefined && {
            financeUserIds: data.finance_user_ids ?? [],
        }),
        ...(data.gl_type !== undefined && { glType: data.gl_type }),
        ...(data.deal_file_type !== undefined && { dealFileType: data.deal_file_type }),
        ...(data.final_file_destination !== undefined && {
            finalFileDestination: data.final_file_destination,
        }),
        ...(data.import_available !== undefined && { importAvailable: data.import_available }),
        ...(data.monthly_deadline_day !== undefined && {
            monthlyDeadlineDay: data.monthly_deadline_day ?? null,
        }),
        isActive: result.is_active,
        activatedAt: result.activated_at ?? null,
        deactivatedAt: result.deactivated_at ?? null,
        updatedAt: result.updated_at,
    };
}

export const wireDealDetail = ({ store, useAppStore, api }: TCompositionDeps) => {
    const ViewDealDetail = createViewDealDetail({
        store,
        useCases: () => {
            const currentUser = useAppStore((state) => state.user);
            const hasPermission = (action: string) =>
                currentUser?.permissions.includes(action) ?? false;

            const canEditDeal = hasPermission("deal_edit");
            const canEditDealName = hasPermission("deal_name_edit");
            const canViewColumns = hasPermission("deal_columns_view");
            const canEditTbColumns = hasPermission("deal_tb_columns_edit");
            const canEditGlColumns = hasPermission("deal_gl_columns_edit");
            const canUploadOtb = hasPermission("deal_create");
            const canCopyDeal = hasPermission("deal_create");
            const canViewPropcode = hasPermission("deal_propcode_view");
            const canUploadPropcode = hasPermission("deal_propcode_upload");
            const canSnapshotPropcode = hasPermission("deal_propcode_snapshot_create");
            const canRestorePropcode = hasPermission("deal_propcode_restore");
            const canEditPropcode = hasPermission("deal_propcode_edit");
            const canDeletePropcode = hasPermission("deal_propcode_delete");

            const [dealState, setDealState] = useState<{
                data: TDeal | null;
                loading: boolean;
                error: string | null;
            }>({ data: null, loading: true, error: null });

            const [businessPartner, setBusinessPartner] = useState<{
                data: { id: string; name: string; code: string } | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [propcodeMappings, setPropcodeMappings] = useState<{
                data: Array<{
                    id: string;
                    kind: string;
                    sourcePropcode: string;
                    targetPropcode: string;
                    isActive: boolean;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: false });

            const [tbColumnMappings, setTbColumnMappings] = useState<{
                data: Array<{
                    id: string;
                    fileType: TApiColumnMappingFileType;
                    fieldType: TApiColumnMappingFieldType;
                    columnName: string;
                    headerRowNumber: number;
                    isActive: boolean;
                    availableColumns: string[] | null;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: false });

            const [glColumnMappings, setGlColumnMappings] = useState<{
                data: Array<{
                    id: string;
                    fileType: TApiColumnMappingFileType;
                    fieldType: TApiColumnMappingFieldType;
                    columnName: string;
                    headerRowNumber: number;
                    isActive: boolean;
                    availableColumns: string[] | null;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: false });

            const [users, setUsers] = useState<{
                data: Awaited<ReturnType<typeof api.users.list>>["items"] | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [otbInfoState, setOtbInfoState] = useState<{
                data: Awaited<ReturnType<typeof api.deals.getOTBInfo>> | null;
                loading: boolean;
            }>({ data: null, loading: false });

            /** Bumped on unmount / effect cleanup so in-flight loads ignore stale results (Strict Mode, fast navigation). */
            const dealDetailLoadGenRef = useRef(0);

            const loadData = useCallback(async (dealId: string, bpId: string) => {
                const loadGen = ++dealDetailLoadGenRef.current;
                const isCurrent = () => loadGen === dealDetailLoadGenRef.current;

                setDealState({ data: null, loading: true, error: null });
                try {
                    const deal = await api.deals.get(dealId);
                    if (!isCurrent()) return;
                    setDealState({
                        data: {
                            id: deal.id,
                            companyId: deal.company_id,
                            name: deal.name,
                            code: deal.code,
                            serviceProviderId: deal.service_provider_id,
                            portfolioCompanyId: deal.portfolio_company_id,
                            financeUserIds: deal.finance_user_ids ?? [],
                            dealFileType: deal.deal_file_type,
                            glType: deal.gl_type,
                            importAvailable: deal.import_available,
                            finalFileDestination: deal.final_file_destination,
                            ...NO_SEGMENTS,
                            monthlyDeadlineDay: deal.monthly_deadline_day,
                            quarterlyDeadlineDay: deal.quarterly_deadline_day,
                            yearEndDeadline: deal.year_end_deadline,
                            startPeriod: deal.start_period,
                            yardiEntityId: deal.yardi_entity_id,
                            yardiDatabase: deal.yardi_database,
                            acceptsOtb: deal.accepts_otb,
                            otbComparisonEnabled: deal.otb_comparison_enabled,
                            acceptsFinJournals: deal.accepts_fin_journals,
                            isActive: deal.is_active,
                            isTest: deal.is_test,
                            notes: deal.notes,
                            tbFilteringRules: (deal.tb_filtering_rules ?? []).map((r) => ({
                                filteringColumnHeader: r.filtering_column_header,
                                filteringValue: r.filtering_value,
                                includeFilteredValues: r.include_filtered_values,
                            })),
                            glFilteringRules: (deal.gl_filtering_rules ?? []).map((r) => ({
                                filteringColumnHeader: r.filtering_column_header,
                                filteringValue: r.filtering_value,
                                includeFilteredValues: r.include_filtered_values,
                            })),
                            activatedAt: deal.activated_at,
                            deactivatedAt: deal.deactivated_at,
                            createdAt: deal.created_at,
                            updatedAt: deal.updated_at,
                        },
                        loading: false,
                        error: null,
                    });
                } catch (err) {
                    if (!isCurrent()) return;
                    setDealState({
                        data: null,
                        loading: false,
                        error: err instanceof Error ? err.message : "Failed to load deal",
                    });
                    return;
                }

                if (!isCurrent()) return;

                setBusinessPartner({ data: null, loading: true });
                try {
                    try {
                        const sp = await api.serviceProviders.get(bpId);
                        if (!isCurrent()) return;
                        setBusinessPartner({
                            data: { id: sp.id, name: sp.name, code: sp.code },
                            loading: false,
                        });
                    } catch {
                        const pc = await api.portfolioCompanies.get(bpId);
                        if (!isCurrent()) return;
                        setBusinessPartner({
                            data: { id: pc.id, name: pc.name, code: pc.code },
                            loading: false,
                        });
                    }
                } catch {
                    if (!isCurrent()) return;
                    setBusinessPartner({ data: null, loading: false });
                }

                if (!isCurrent()) return;

                setPropcodeMappings({ data: null, loading: true });
                try {
                    const propMappingItems = await fetchAllPages((skip, limit) =>
                        api.deals.getPropcodeMappings(dealId, { skip, limit }),
                    );
                    if (!isCurrent()) return;
                    setPropcodeMappings({
                        data: propMappingItems.map((m) => ({
                            id: m.id,
                            kind: m.kind ?? "",
                            sourcePropcode: m.source_property_code,
                            targetPropcode: m.target_property_code,
                            isActive: m.is_active,
                        })),
                        loading: false,
                    });
                } catch {
                    if (!isCurrent()) return;
                    setPropcodeMappings({ data: [], loading: false });
                }

                if (!isCurrent()) return;

                setTbColumnMappings({ data: null, loading: true });
                setGlColumnMappings({ data: null, loading: true });
                setUsers({ data: null, loading: true });
                setOtbInfoState({ data: null, loading: true });

                // Master property codes are deliberately absent here: the Propcode
                // dropdowns search them server-side per keystroke, so preloading a
                // capped first page would only re-introduce a ceiling on what the
                // dropdowns can find.
                const parallelDealResources = await Promise.allSettled([
                    api.deals.getColumnMappings(dealId, { file_type: "tb" }),
                    api.deals.getColumnMappings(dealId, { file_type: "gl" }),
                    api.users.list({ finance_team: true, page_size: 1000 }),
                    api.deals.getOTBInfo(dealId),
                ]);

                if (!isCurrent()) return;

                const [tbRes, glRes, usersRes, otbRes] = parallelDealResources;

                if (tbRes.status === "fulfilled") {
                    const tbMappings = tbRes.value.items;
                    setTbColumnMappings({
                        data: tbMappings.map((m) => ({
                            id: m.id,
                            fileType: m.file_type,
                            fieldType: m.field_type,
                            columnName: m.column_name,
                            headerRowNumber: m.header_row_number,
                            isActive: m.is_active,
                            availableColumns: m.available_columns,
                        })),
                        loading: false,
                    });
                } else {
                    setTbColumnMappings({ data: [], loading: false });
                }

                if (glRes.status === "fulfilled") {
                    const glMappings = glRes.value.items;
                    setGlColumnMappings({
                        data: glMappings.map((m) => ({
                            id: m.id,
                            fileType: m.file_type,
                            fieldType: m.field_type,
                            columnName: m.column_name,
                            headerRowNumber: m.header_row_number,
                            isActive: m.is_active,
                            availableColumns: m.available_columns,
                        })),
                        loading: false,
                    });
                } else {
                    setGlColumnMappings({ data: [], loading: false });
                }

                if (usersRes.status === "fulfilled") {
                    setUsers({ data: usersRes.value.items, loading: false });
                } else {
                    setUsers({ data: [], loading: false });
                }

                if (otbRes.status === "fulfilled") {
                    setOtbInfoState({ data: otbRes.value, loading: false });
                } else {
                    setOtbInfoState({ data: null, loading: false });
                }
            }, []);

            const onUpdateDeal = useCallback(
                async (dealId: string, data: Parameters<typeof api.deals.update>[1]) => {
                    const result = await api.deals.update(dealId, data);
                    setDealState((prev) => {
                        if (!prev.data) return prev;
                        return { ...prev, data: applyDealUpdate(prev.data, data, result) };
                    });
                },
                [],
            );

            // Filtering rules have their own column-edit-gated endpoint; they are not
            // part of the deal update payload. The server response is authoritative,
            // so the local deal state is refreshed from it rather than from the input.
            const onUpdateFilteringRules = useCallback(
                async (dealId: string, fileType: "tb" | "gl", rules: TFilteringRule[]) => {
                    const result = await api.deals.updateFilteringRules(dealId, {
                        file_type: fileType,
                        rules: rules.map((r) => ({
                            filtering_column_header: r.filteringColumnHeader,
                            filtering_value: r.filteringValue,
                            include_filtered_values: r.includeFilteredValues,
                        })),
                    });
                    const saved = result.rules.map((r) => ({
                        filteringColumnHeader: r.filtering_column_header,
                        filteringValue: r.filtering_value,
                        includeFilteredValues: r.include_filtered_values,
                    }));
                    setDealState((prev) => {
                        if (!prev.data) return prev;
                        return {
                            ...prev,
                            data: {
                                ...prev.data,
                                ...(fileType === "tb"
                                    ? { tbFilteringRules: saved }
                                    : { glFilteringRules: saved }),
                            },
                        };
                    });
                    return saved;
                },
                [],
            );

            const propcodeVersionActions = useDealDetailPropcodeVersionActions(store, api);
            const mappingActions = useDealDetailMappingActions(
                api,
                setTbColumnMappings,
                setGlColumnMappings,
            );

            return {
                onMount: loadData,
                onUnmount: useCallback(() => {
                    dealDetailLoadGenRef.current += 1;
                }, []),
                useDealState: () => dealState,
                useBusinessPartner: () => businessPartner,
                usePropcodeMappings: () => propcodeMappings,
                // Master codes are listed per kind: a mapping's target must be a code of its kind.
                searchMasterPropertyCodes: useCallback(
                    (query: string, kind: string) =>
                        searchMasterPropertyCodes(
                            {
                                listPaginated: (params) =>
                                    api.propertyCodes.listPaginated({ ...params, kind }),
                            },
                            query,
                        ),
                    [api.propertyCodes],
                ),
                propcodeVersionActions,
                onCreatePropcodeMapping: mappingActions.onCreatePropcodeMapping,
                onUpdatePropcodeMapping: mappingActions.onUpdatePropcodeMapping,
                onDeletePropcodeMapping: mappingActions.onDeletePropcodeMapping,
                useColumnMappings: (fileType: "tb" | "gl") =>
                    fileType === "tb" ? tbColumnMappings : glColumnMappings,
                columnMappingActions: mappingActions.columnMappingActions,
                onUpdateDeal,
                onUpdateFilteringRules,
                useUsers: () => users,
                useOTBInfo: () => otbInfoState,
                onUploadOTB: useCallback(async (dealId: string, file: File) => {
                    return await api.deals.uploadOTB(dealId, file);
                }, []),
                onRefreshOTBInfo: useCallback(async (dealId: string) => {
                    setOtbInfoState({ data: null, loading: true });
                    try {
                        const info = await api.deals.getOTBInfo(dealId);
                        setOtbInfoState({ data: info, loading: false });
                    } catch {
                        setOtbInfoState({ data: null, loading: false });
                    }
                }, []),
                canEditDeal,
                canEditDealName,
                canViewColumns,
                canEditTbColumns,
                canEditGlColumns,
                canUploadOtb,
                canCopyDeal,
                canViewPropcode,
                canUploadPropcode,
                canSnapshotPropcode,
                canRestorePropcode,
                canEditPropcode,
                canDeletePropcode,
            };
        },
    });

    return { ViewDealDetail };
};
